import React, { useEffect, useMemo, useState } from "react";
import {
    Container,
    Paper,
    Stack,
    Group,
    Title,
    Text,
    Badge,
    Table,
    Loader,
    Center,
    ActionIcon,
    Tooltip,
    ScrollArea,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import {
    IconPlaneArrival,
    IconPlaneDeparture,
    IconCircleCheck,
    IconClock,
    IconX,
    IconCircle,
    IconEdit,
} from "@tabler/icons-react";
import { useParams } from "react-router-dom";

import { PublicTransportBookingReadDto } from "../../types/transportPublicTypes";
import { getMyTransportBookings } from "../../api/transportPublicApi";
import EditMyBookingDrawer from "./EditMyBookingDrawer";

const RED = "#ff5e5e";

function kindToString(kind: number | null | undefined): string {
    switch (kind) {
        case 1:
            return "arrival";
        case 2:
            return "departure";
        case 3:
            return "local";
        default:
            return "";
    }
}

function kindMeta(kind: number | string | null | undefined) {
    const value =
        typeof kind === "number"
            ? kindToString(kind)
            : String(kind ?? "").toLowerCase();

    switch (value) {
        case "arrival":
            return {
                label: "Arrival",
                icon: <IconPlaneArrival size={16} />,
            };

        case "departure":
            return {
                label: "Departure",
                icon: <IconPlaneDeparture size={16} />,
            };

        case "local":
            return {
                label: "Local",
                icon: <span style={{ fontSize: 16 }}>🚗</span>,
            };

        default:
            return {
                label: "Unknown",
                icon: <span style={{ fontSize: 16 }}>❓</span>,
            };
    }
}

function isArrival(kind: unknown) {
    return kind === 1 || String(kind).toLowerCase() === "arrival";
}

function isDeparture(kind: unknown) {
    return kind === 2 || String(kind).toLowerCase() === "departure";
}

function fromToForRow(booking: PublicTransportBookingReadDto) {
    const pickup = booking.pickUpPlace ?? "-";
    const dropoff = booking.dropOffPlace ?? "-";
    const flight = booking.flightNo || "Flight";

    if (isArrival(booking.kind)) {
        return {
            fromText: flight,
            toText: dropoff,
        };
    }

    if (isDeparture(booking.kind)) {
        return {
            fromText: pickup,
            toText: flight,
        };
    }

    return {
        fromText: pickup,
        toText: dropoff,
    };
}

function statusMetaFromId(id?: number | null) {
    switch (id) {
        case 3:
            return {
                label: "Confirmed",
                color: "green" as const,
                icon: <IconCircleCheck size={14} />,
            };

        case 2:
            return {
                label: "Delayed",
                color: "orange" as const,
                icon: <IconClock size={14} />,
            };

        case 4:
            return {
                label: "Cancelled",
                color: "red" as const,
                icon: <IconX size={14} />,
            };

        default:
            return {
                label: "Booked",
                color: "gray" as const,
                icon: <IconCircle size={14} />,
            };
    }
}

export default function TransportMyBookings() {
    const { userId, eventId } = useParams<{
        userId: string;
        eventId: string;
    }>();

    const userIdNum = Number(userId);
    const eventIdNum = Number(eventId);

    const [loading, setLoading] = useState(true);

    const [bookings, setBookings] = useState<
        PublicTransportBookingReadDto[]
    >([]);

    const [drawerOpen, setDrawerOpen] = useState(false);

    const [selected, setSelected] =
        useState<PublicTransportBookingReadDto | null>(null);

    const totalBookings = useMemo(
        () => bookings.length,
        [bookings]
    );

    const totalPassengers = useMemo(
        () =>
            bookings.reduce(
                (sum, booking) =>
                    sum + (booking.numberOfPersons ?? 0),
                0
            ),
        [bookings]
    );

    const fetchBookings = async () => {
        if (
            Number.isNaN(userIdNum) ||
            Number.isNaN(eventIdNum)
        ) {
            showNotification({
                color: "red",
                title: "Ugyldigt link",
                message:
                    "Linket skal indeholde både userId og eventId.",
            });

            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const data = await getMyTransportBookings(
                userIdNum,
                eventIdNum
            );

            setBookings(data);
        } catch (error: any) {
            showNotification({
                color: "red",
                title: "Fejl",
                message:
                    error?.response?.data?.message ||
                    error?.response?.data ||
                    "Kunne ikke hente dine transportbookinger.",
                autoClose: 8000,
            });

            setBookings([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBookings();
    }, [userIdNum, eventIdNum]);

    const openEdit = (
        booking: PublicTransportBookingReadDto
    ) => {
        setSelected(booking);
        setDrawerOpen(true);
    };

    const CELL_BASE: React.CSSProperties = {
        borderTop: "1px solid rgba(0,0,0,0.06)",
        borderBottom: "1px solid rgba(0,0,0,0.06)",
        background: "white",
    };

    return (
        <Container size="xl" py="xl">
            <Stack gap="md">
                <Paper withBorder radius="md" p="lg">
                    <Group justify="space-between">
                        <Stack gap={2}>
                            <Title order={3}>
                                My transport bookings
                            </Title>

                            <Text c="dimmed" size="sm">
                                View and edit your existing
                                transport bookings
                            </Text>
                        </Stack>

                        <Group gap="xs">
                            <Badge variant="light" color="red">
                                {totalBookings} bookings
                            </Badge>

                            <Badge variant="light" color="red">
                                {totalPassengers} pax
                            </Badge>
                        </Group>
                    </Group>
                </Paper>

                <Paper withBorder radius="md" p="lg">
                    <Group
                        justify="space-between"
                        mb="sm"
                    >
                        <Title order={4}>Bookings</Title>

                        {loading && <Loader size="sm" />}
                    </Group>

                    {loading ? (
                        <Center py="xl">
                            <Loader />
                        </Center>
                    ) : bookings.length === 0 ? (
                        <Text c="dimmed">
                            Der blev ikke fundet nogen
                            transportbookinger for denne bruger
                            og dette event.
                        </Text>
                    ) : (
                        <ScrollArea>
                            <Table
                                highlightOnHover
                                verticalSpacing="sm"
                                horizontalSpacing="md"
                                withRowBorders={false}
                                style={{
                                    borderCollapse: "separate",
                                    borderSpacing: "0 10px",
                                }}
                            >
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th>Kind</Table.Th>
                                        <Table.Th>Date</Table.Th>
                                        <Table.Th>Time</Table.Th>
                                        <Table.Th>
                                            From → To
                                        </Table.Th>
                                        <Table.Th>Contact</Table.Th>
                                        <Table.Th>Status</Table.Th>
                                        <Table.Th>Pax</Table.Th>
                                        <Table.Th
                                            style={{
                                                width: 60,
                                                textAlign: "right",
                                            }}
                                        />
                                    </Table.Tr>
                                </Table.Thead>

                                <Table.Tbody>
                                    {bookings.map((booking) => {
                                        const kind = kindMeta(
                                            booking.kind
                                        );

                                        const status =
                                            statusMetaFromId(
                                                booking.statusTransportId
                                            );

                                        const fromTo =
                                            fromToForRow(booking);

                                        const firstCellStyle:
                                            React.CSSProperties = {
                                            ...CELL_BASE,
                                            borderLeft:
                                                "1px solid rgba(0,0,0,0.06)",
                                            borderTopLeftRadius: 10,
                                            borderBottomLeftRadius: 10,
                                            paddingLeft: 14,
                                        };

                                        const middleCellStyle:
                                            React.CSSProperties = {
                                            ...CELL_BASE,
                                        };

                                        const lastCellStyle:
                                            React.CSSProperties = {
                                            ...CELL_BASE,
                                            borderRight:
                                                "1px solid rgba(0,0,0,0.06)",
                                            borderTopRightRadius: 10,
                                            borderBottomRightRadius: 10,
                                            textAlign: "right",
                                            paddingRight: 10,
                                        };

                                        return (
                                            <Table.Tr
                                                key={
                                                    booking.transportRequestId
                                                }
                                            >
                                                <Table.Td
                                                    style={
                                                        firstCellStyle
                                                    }
                                                >
                                                    <Group
                                                        gap={6}
                                                        wrap="nowrap"
                                                    >
                                                        {kind.icon}

                                                        <Text
                                                            size="sm"
                                                            fw={700}
                                                        >
                                                            {kind.label}
                                                        </Text>
                                                    </Group>

                                                    {booking.flightNo && (
                                                        <Text
                                                            size="xs"
                                                            c="dimmed"
                                                        >
                                                            Flight:{" "}
                                                            {
                                                                booking.flightNo
                                                            }
                                                        </Text>
                                                    )}
                                                </Table.Td>

                                                <Table.Td
                                                    style={
                                                        middleCellStyle
                                                    }
                                                >
                                                    <Text size="sm">
                                                        {booking.scheduledDate
                                                            ? booking.scheduledDate.slice(
                                                                0,
                                                                10
                                                            )
                                                            : "-"}
                                                    </Text>
                                                </Table.Td>

                                                <Table.Td
                                                    style={
                                                        middleCellStyle
                                                    }
                                                >
                                                    <Text size="sm">
                                                        {booking.scheduledTime ??
                                                            "-"}
                                                    </Text>
                                                </Table.Td>

                                                <Table.Td
                                                    style={
                                                        middleCellStyle
                                                    }
                                                >
                                                    <Text
                                                        size="sm"
                                                        fw={700}
                                                    >
                                                        {
                                                            fromTo.fromText
                                                        }
                                                    </Text>

                                                    <Text
                                                        size="xs"
                                                        c="dimmed"
                                                    >
                                                        →{" "}
                                                        {fromTo.toText}
                                                    </Text>
                                                </Table.Td>

                                                <Table.Td
                                                    style={
                                                        middleCellStyle
                                                    }
                                                >
                                                    <Text
                                                        size="sm"
                                                        fw={700}
                                                    >
                                                        {booking.contactPerson ??
                                                            "-"}
                                                    </Text>

                                                    <Text
                                                        size="xs"
                                                        c="dimmed"
                                                    >
                                                        {booking.contactPersonEmail ??
                                                            "-"}
                                                    </Text>
                                                </Table.Td>

                                                <Table.Td
                                                    style={
                                                        middleCellStyle
                                                    }
                                                >
                                                    <Badge
                                                        variant="light"
                                                        color={
                                                            status.color
                                                        }
                                                        leftSection={
                                                            status.icon
                                                        }
                                                    >
                                                        {
                                                            status.label
                                                        }
                                                    </Badge>
                                                </Table.Td>

                                                <Table.Td
                                                    style={
                                                        middleCellStyle
                                                    }
                                                >
                                                    <Badge
                                                        variant="light"
                                                        color="red"
                                                    >
                                                        {booking.numberOfPersons ??
                                                            0}
                                                    </Badge>
                                                </Table.Td>

                                                <Table.Td
                                                    style={
                                                        lastCellStyle
                                                    }
                                                >
                                                    <Tooltip label="Rediger booking">
                                                        <ActionIcon
                                                            variant="light"
                                                            onClick={() =>
                                                                openEdit(
                                                                    booking
                                                                )
                                                            }
                                                            style={{
                                                                color: RED,
                                                            }}
                                                        >
                                                            <IconEdit
                                                                size={
                                                                    18
                                                                }
                                                            />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                </Table.Td>
                                            </Table.Tr>
                                        );
                                    })}
                                </Table.Tbody>
                            </Table>
                        </ScrollArea>
                    )}
                </Paper>
            </Stack>

            <EditMyBookingDrawer
                opened={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                booking={selected}
                eventId={eventIdNum}
                userId={userIdNum}
                onSaved={fetchBookings}
            />
        </Container>
    );
}